// src/components/SurveyForm/Questions/BigSelectionQuestion/BigSelectionQuestion.jsx

import React, { Fragment } from 'react';
import {
  Autocomplete,
  Box,
  Divider,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
/**
 * Componente para preguntas de selección grande (Autocomplete)
 * Soporta modo único y modo múltiple con límites opcionales
 */
const BigSelectionQuestion = ({
  questionName,
  description,
  options = [],
  settings = null,
  formValue = {},
  index,
  questionId,
  conditional = false,
  unansweredQuestions = [],
  questionRef,
  onValueChange,
  onVisibilityUpdate,
  fontFamily,
  hasOwnBold,
}) => {
  // ✅ Determinar si es modo múltiple
  const isMultipleMode = () => {
    if (!settings) return false; // Sin settings → modo único
    if (settings.haveLimit === false) return true; // Sin límite → múltiple
    if (settings.haveLimit === true) {
      if (!settings.limitOptions) return false; // Sin limitOptions → único
      if (settings.limitOptions === 1) return false; // Límite 1 → único
      return true; // Límite > 1 → múltiple
    }
    return false; // Default → único
  };

  const isMultiple = isMultipleMode();

  // ✅ Obtener el valor actual según el modo
  const getCurrentValue = () => {
    if (isMultiple) {
      // MODO MÚLTIPLE → Devolver ARRAY de strings seleccionados
      return Object.keys(formValue?.values || {}).filter(
        (key) => formValue?.values[key] === true,
      );
    } else {
      // MODO ÚNICO → Devolver STRING o ""
      return formValue?.value || '';
    }
  };

  // ✅ Manejar cambios de selección
  const handleChange = (event, newValue) => {
    if (!isMultiple) {
      // ============================================
      // MODO ÚNICO
      // ============================================
      const selectedOption = options.find((opt) => opt.optionName === newValue);

      onValueChange({
        value: newValue || '',
        values: formValue?.values || {},
      });

      // Actualizar visibilidad si es condicional
      if (conditional && selectedOption) {
        onVisibilityUpdate(selectedOption.childrenQuestionNumber);
      }
    } else {
      // ============================================
      // MODO MÚLTIPLE
      // ============================================

      // Verificar límite si existe
      if (
        settings?.haveLimit === true &&
        settings?.limitOptions &&
        newValue.length > settings.limitOptions
      ) {
        return; // No permitir más de lo permitido
      }

      // Convertir array a objeto {opcion: true/false}
      const valuesObject = {};
      options.forEach((opt) => {
        valuesObject[opt.optionName] = newValue.includes(opt.optionName);
      });

      onValueChange({
        value: formValue?.value || '',
        values: valuesObject,
      });
    }
  };

  // ✅ Obtener label del input
  const getInputLabel = () => {
    if (
      !settings ||
      (settings?.haveLimit === true && settings?.limitOptions === 1)
    ) {
      return 'Selecciona una opción';
    }
    if (settings?.haveLimit === true && settings?.limitOptions > 1) {
      return `Selecciona hasta ${settings.limitOptions} opciones`;
    }
    return 'Selecciona opciones (ilimitado)';
  };

  // ✅ Obtener helper text
  const getHelperText = () => {
    if (unansweredQuestions.includes(index)) {
      return 'Este campo es requerido';
    }

    if (!isMultiple) {
      return null;
    }

    const selectedCount = Object.values(formValue?.values || {}).filter(
      (v) => v === true,
    ).length;

    if (settings?.haveLimit === true && settings?.limitOptions > 1) {
      return `${selectedCount}/${settings.limitOptions} seleccionadas`;
    }

    if (settings?.haveLimit === false) {
      return `${selectedCount} seleccionadas`;
    }

    return null;
  };

  // ✅ Obtener color del contador
  const getCounterColor = () => {
    if (
      settings?.haveLimit === true &&
      settings?.limitOptions &&
      Object.values(formValue?.values || {}).filter((v) => v === true).length >=
        settings.limitOptions
    ) {
      return 'error.main';
    }
    return 'text.secondary';
  };

  // ✅ Obtener peso del contador
  const getCounterWeight = () => {
    if (
      settings?.haveLimit === true &&
      settings?.limitOptions &&
      Object.values(formValue?.values || {}).filter((v) => v === true).length >=
        settings.limitOptions
    ) {
      return 'bold';
    }
    return 'normal';
  };

  // ✅ Determinar si mostrar contador adicional
  const shouldShowCounter = () => {
    return (
      settings?.haveLimit === false ||
      (settings?.haveLimit === true &&
        settings?.limitOptions &&
        settings?.limitOptions > 1)
    );
  };

  const hasError = unansweredQuestions.includes(index);

  return (
    <Fragment>
      <FormLabel
        style={{
          fontSize: '1.1em',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '1.1em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
        ref={questionRef}
      >
        <span
          className="ql-editor"
          style={{ padding: 0 }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }}
        />
      </FormLabel>

      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
          <span
            className="ql-editor"
            style={{ padding: 0 }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(description),
            }}
          />
        </Typography>
      )}

      <Autocomplete
        multiple={isMultiple}
        options={options.map((opt) => opt.optionName)}
        value={getCurrentValue()}
        onChange={handleChange}
        componentsProps={{
          paper: {
            sx: {
              fontFamily,
              '& .MuiAutocomplete-option': {
                fontFamily,
              },
            },
          },
        }}
        renderOption={(props, option) => (
          <li {...props} translate="no">
            {option}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={getInputLabel()}
            placeholder="Buscar..."
            error={hasError}
            helperText={getHelperText()}
            inputProps={{
              ...params.inputProps,
              translate: 'no',
            }}
          />
        )}
        sx={{
          mt: 1,
          mb: 2,
          backgroundColor: hasError ? '#ffe6e6' : 'white',
        }}
        limitTags={2}
      />

      {/* Contador adicional (solo para múltiples) */}
      {shouldShowCounter() && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: -1.5,
            mb: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: getCounterColor(),
              fontWeight: getCounterWeight(),
            }}
          >
            {settings?.haveLimit === true && settings?.limitOptions
              ? `${
                  Object.values(formValue?.values || {}).filter(
                    (v) => v === true,
                  ).length
                }/${settings.limitOptions} seleccionadas`
              : `${
                  Object.values(formValue?.values || {}).filter(
                    (v) => v === true,
                  ).length
                } seleccionadas`}
          </Typography>
        </Box>
      )}

      <Divider variant="middle" sx={{ marginTop: '10px' }} />
    </Fragment>
  );
};

BigSelectionQuestion.propTypes = {
  questionName: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      optionName: PropTypes.string.isRequired,
      childrenQuestionNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
  ).isRequired,
  settings: PropTypes.shape({
    haveLimit: PropTypes.bool,
    limitOptions: PropTypes.number,
  }),
  formValue: PropTypes.shape({
    value: PropTypes.string,
    values: PropTypes.object,
  }),
  index: PropTypes.number.isRequired,
  questionId: PropTypes.number.isRequired,
  conditional: PropTypes.bool,
  unansweredQuestions: PropTypes.arrayOf(PropTypes.number),
  questionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onValueChange: PropTypes.func.isRequired,
  onVisibilityUpdate: PropTypes.func,
};

export default BigSelectionQuestion;
